import * as THREE from 'three';

/**
 * Screen-space LOD for LandXML TIN surfaces.
 *
 * A survey TIN is tessellated for accuracy, not for display: the corridor in
 * `26-OB-0402` is 16.3 M triangles over 11 layers, which is ~13 triangles per
 * PIXEL at a whole-site zoom. Measured on an M-series Mac (headless Chrome,
 * same renderer options as `Viewer.tsx`), submitting all of it costs 4.9 ms of
 * every 16.7 ms frame with the site framed, and 1.3-2.4 ms zoomed in — before
 * the point clouds, splats and IFC models in the same scene get their turn.
 * That is where the viewer's frame budget goes.
 *
 * The fix is to draw a decimated copy whenever the decimation is too fine to
 * see. `buildTinLod` clusters vertices onto an XY grid whose cell is chosen so
 * the level lands under a triangle budget; `LandXML.tsx` then swaps to it per
 * frame while that cell projects to less than `LOD_MAX_CELL_PX` pixels, so the
 * swap is by construction invisible. Same corridor, same measurements:
 * 4.9 ms -> 0.8 ms framed, 1.3 ms -> 0.4 ms zoomed in.
 *
 * Alternatives measured and rejected: splitting each TIN into an 8x8 or 16x16
 * grid of sub-meshes so frustum culling can drop the off-screen parts helps
 * only when zoomed in (4.9 -> 4.1 ms framed, and 16x16 is a net LOSS at
 * 5.6 ms — the extra draw calls cost more than the culling saves). Decimation
 * beats it everywhere, and combining the two is worse than decimation alone.
 *
 * Clustering is done in XY only, because a TIN is a heightfield: the cluster
 * representative is the average of the cell's vertices, so a vertical face
 * (an embankment wall modelled as near-vertical triangles) collapses toward a
 * single vertex. That is acceptable HERE and only here — the level is shown
 * exclusively at zooms where its whole cell is under ~1.5 px, so the collapsed
 * face is sub-pixel too. Never use these geometries for measurement or
 * analysis; the full-resolution geometry stays on the feature, and
 * `drapeSource` (absolute coordinates, never decimated) remains the source of
 * truth for draping, crest extraction and the volume tools.
 */

/**
 * Triangles per surface the coarse level aims for. 300 k over the 11-layer
 * corridor is ~3.3 M triangles total, which measured at 0.6 ms/frame — a
 * roughly 8x saving for ~80 MB of extra buffers.
 */
export const LOD_TRIANGLE_BUDGET = 300_000;

/**
 * Use the coarse level while its grid cell covers fewer than this many pixels.
 * At 1.5 px a cell is at (or below) the size of the anti-aliasing kernel, so
 * the level switch has nothing left to show.
 */
export const LOD_MAX_CELL_PX = 1.5;

export interface TinLod {
  geometry: THREE.BufferGeometry;
  /** XY grid cell this level was clustered on, in world units (metres). */
  cellSize: number;
}

const triangleCountOf = (geometry: THREE.BufferGeometry): number => {
  const index = geometry.getIndex();
  if (index) return Math.floor(index.count / 3);
  return Math.floor((geometry.getAttribute('position')?.count ?? 0) / 3);
};

/**
 * Grid cell that should bring `geometry` under `budget` triangles, or null
 * when decimating it cannot pay for itself.
 *
 * Estimated rather than searched: a TIN has ~2 triangles per vertex, and
 * clustering an area `A` onto cells of size `c` leaves ~A/c² vertices, so
 * `c = sqrt(2A/budget)`. One clustering pass over 8 M vertices costs ~800 ms,
 * so iterating to hit the budget exactly is not worth the load-time stall —
 * the budget is a target, not a contract.
 */
export const chooseCellSize = (
  geometry: THREE.BufferGeometry,
  budget: number = LOD_TRIANGLE_BUDGET,
): number | null => {
  if (budget <= 0) return null;
  const position = geometry.getAttribute('position');
  if (!position || position.count === 0) return null;
  if (triangleCountOf(geometry) <= budget) return null;

  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) return null;
  const spanX = box.max.x - box.min.x;
  const spanY = box.max.y - box.min.y;
  const area = spanX * spanY;
  if (!Number.isFinite(area) || area <= 0) return null;

  const cell = Math.sqrt((2 * area) / budget);
  // Below the TIN's own vertex spacing there is nothing left to merge, and the
  // level would cost memory for a copy of the original.
  const sourceSpacing = Math.sqrt(area / position.count);
  if (cell <= sourceSpacing * 1.5) return null;
  return cell;
};

/**
 * Cluster `geometry`'s vertices onto an XY grid of `cellSize` and rebuild the
 * triangles against the merged vertices, dropping the ones that collapse.
 *
 * Works on indexed geometry (the fast TIN extractor's output) and on
 * non-indexed triangle soup (the DOM parser's fallback path) alike; the result
 * is always indexed. `userData` is carried over because the double-click pick
 * in `LandXML.tsx` recovers the feature id from the geometry it hit.
 */
export const decimateTin = (
  geometry: THREE.BufferGeometry,
  cellSize: number,
): THREE.BufferGeometry | null => {
  if (!(cellSize > 0)) return null;
  const position = geometry.getAttribute('position');
  if (!position || position.count === 0) return null;
  const src = position.array as ArrayLike<number>;

  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) return null;
  const originX = box.min.x;
  const originY = box.min.y;
  const columns = Math.max(1, Math.ceil((box.max.x - originX) / cellSize));

  // Pass 1: map every source vertex to a cluster, accumulating the cluster's
  // centroid as we go. `cluster[v]` is the new vertex index for source vertex v.
  const cluster = new Uint32Array(position.count);
  const byCell = new Map<number, number>();
  const sums: number[] = [];
  for (let v = 0; v < position.count; v++) {
    const o = v * 3;
    const cx = Math.floor((src[o] - originX) / cellSize);
    const cy = Math.floor((src[o + 1] - originY) / cellSize);
    const key = cy * columns + cx;
    let next = byCell.get(key);
    if (next === undefined) {
      next = sums.length / 4;
      byCell.set(key, next);
      sums.push(0, 0, 0, 0);
    }
    const s = next * 4;
    sums[s] += src[o];
    sums[s + 1] += src[o + 1];
    sums[s + 2] += src[o + 2];
    sums[s + 3] += 1;
    cluster[v] = next;
  }

  const vertexCount = sums.length / 4;
  if (vertexCount < 3) return null;
  const positions = new Float32Array(vertexCount * 3);
  for (let i = 0; i < vertexCount; i++) {
    const s = i * 4;
    const n = sums[s + 3] || 1;
    positions[i * 3] = sums[s] / n;
    positions[i * 3 + 1] = sums[s + 1] / n;
    positions[i * 3 + 2] = sums[s + 2] / n;
  }

  // Pass 2: rewrite the triangles. A triangle whose corners landed in fewer
  // than three distinct cells has no area left and is dropped — that is where
  // the reduction comes from.
  const index = geometry.getIndex();
  const triangles = triangleCountOf(geometry);
  const out = new Uint32Array(triangles * 3);
  let write = 0;
  for (let t = 0; t < triangles; t++) {
    const a = cluster[index ? index.getX(t * 3) : t * 3];
    const b = cluster[index ? index.getX(t * 3 + 1) : t * 3 + 1];
    const c = cluster[index ? index.getX(t * 3 + 2) : t * 3 + 2];
    if (a === b || b === c || a === c) continue;
    out[write++] = a;
    out[write++] = b;
    out[write++] = c;
  }
  if (write === 0) return null;

  const decimated = new THREE.BufferGeometry();
  decimated.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  decimated.setIndex(new THREE.BufferAttribute(out.slice(0, write), 1));
  decimated.computeVertexNormals();
  decimated.userData = { ...(geometry.userData ?? {}) };
  return decimated;
};

/**
 * Build the coarse level for one TIN, or null when the surface is already
 * small enough (or too degenerate) to be worth one.
 */
export const buildTinLod = (
  geometry: THREE.BufferGeometry,
  budget: number = LOD_TRIANGLE_BUDGET,
): TinLod | null => {
  const cellSize = chooseCellSize(geometry, budget);
  if (cellSize === null) return null;
  const decimated = decimateTin(geometry, cellSize);
  if (!decimated) return null;
  // A level that saved nothing is pure memory. Can happen on a TIN whose
  // vertices are already spread wider than the estimate assumed.
  if (triangleCountOf(decimated) >= triangleCountOf(geometry) * 0.8) {
    decimated.dispose();
    return null;
  }
  return { geometry: decimated, cellSize };
};

/**
 * World units per screen pixel at `target`, for either camera type.
 *
 * Camera distance is meaningless under the orthographic camera `Viewer.tsx`
 * dollies to 1000 m — see the screen-space sizing rule in CLAUDE.md. This is
 * the same computation the picking tools' markers use, kept here so the LOD
 * switch is driven by what the user can actually resolve on screen.
 */
export const unitsPerPixelAt = (
  camera: THREE.Camera,
  target: THREE.Vector3,
  viewportHeight: number,
): number => {
  if (viewportHeight <= 0) return 0;
  if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
    const ortho = camera as THREE.OrthographicCamera;
    const worldHeight = Math.abs(ortho.top - ortho.bottom) / Math.max(ortho.zoom, 1e-6);
    return worldHeight / viewportHeight;
  }
  const persp = camera as THREE.PerspectiveCamera;
  const distance = camera.position.distanceTo(target);
  const fov = (persp.getEffectiveFOV?.() ?? persp.fov) * (Math.PI / 180);
  return (2 * distance * Math.tan(fov / 2)) / viewportHeight;
};
